'use strict'

import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { AutoSizer, Column, Table } from 'react-virtualized'
import { useConversionTableSelector } from '../extra/store'
import { getTableKey } from '../extra/helpers'

const ROW_HEIGHT = 44
const HEADER_HEIGHT = 40
const OVERSCAN_ROWS = 8
// 区分マスター（setting-table）と同等のセル左右余白(16px)。仮想化テーブルの既定(2px/4px)は詳細度が高いため important で上書きする。
const CELL_CLASS = '!px-4'

/**
 * 変換項目テーブルコンポーネント
 *
 * @param {Object} props
 * @param {string} props.pendingConversionTarget - 右ペインで選択中の変換先区分（なしは空文字）
 * @param {function(string): void} props.onConversionTargetChange - 変換先区分変更時
 * @returns {JSX.Element}
 */
const ConversionItemTable = ({ pendingConversionTarget, onConversionTargetChange }) => {
  const state = useConversionTableSelector((s) => s)
  const selectedTable = state.selectedTable
  const previewItems = state.previewItems
  const conversionItems = state.conversionItems
  const validationErrors = state.validationErrors
  const isLoading = state.isLoading
  const classifications = state.classifications

  const selectedMeta = useMemo(() => {
    return selectedTable
      ? state.tableList.find((t) => getTableKey(t) === getTableKey(selectedTable)) || selectedTable
      : null
  }, [selectedTable, state.tableList])

  const classificationOptions = useMemo(() => {
    return [
      { value: '', label: 'なし' },
      ...(classifications || []).map((c) => ({
        value: c.search_key ?? c.SearchKey ?? c.ClassificationKey ?? '',
        label:
          c.data_classification_name ??
          c.DataClassificationName ??
          c.search_key ??
          c.SearchKey ??
          c.ClassificationKey ??
          'なし'
      }))
    ]
  }, [classifications])

  const isPreviewMode = previewItems !== null
  const displayItems = isPreviewMode ? previewItems : conversionItems
  const isEmpty = !selectedTable

  const errorByRow = useMemo(() => {
    if (!validationErrors || validationErrors.length === 0) {
      return new Map()
    }

    const map = new Map()
    validationErrors.forEach((err) => {
      const row = err.rowIndex ?? err.row
      if (!map.has(row)) {
        map.set(row, [])
      }
      map.get(row).push(err)
    })
    return map
  }, [validationErrors])

  // 行番号セル（No.）。仮想化テーブルでは index から算出する。
  const noCellRenderer = useCallback(({ rowIndex }) => rowIndex + 1, [])

  // 変換前 / 変換後セル。エラーがあれば赤字メッセージを省略表示し、全文はツールチップ（title）で見せる。
  const valueCellRenderer = useCallback(
    ({ dataKey, rowData, rowIndex }) => {
      const value = rowData?.[dataKey] ?? ''
      const rowErrors = errorByRow.get(rowIndex + 1) || []
      const errMsg = rowErrors.find((e) => e.field === dataKey)?.message
      if (!errMsg) {
        return (
          <span className="truncate" title={String(value)}>
            {value}
          </span>
        )
      }
      return (
        <span className="flex items-center gap-2 min-w-0 w-full" title={`${value}　${errMsg}`}>
          <span className="truncate">{value}</span>
          <span className="text-red-600 truncate">{errMsg}</span>
        </span>
      )
    },
    [errorByRow]
  )

  const headerRenderer = useCallback(
    ({ label }) => (
      <span className="truncate" title={label}>
        {label}
      </span>
    ),
    []
  )

  // エラー行は赤背景でハイライト（index=-1 はヘッダ行）。
  const rowClassName = useCallback(
    ({ index }) => (index >= 0 && errorByRow.has(index + 1) ? 'bg-red-50' : ''),
    [errorByRow]
  )

  if (isEmpty) {
    return (
      <div className="flex flex-col" style={{ flex: 1, minHeight: 0 }} data-testid="conversion-item-table">
        <div className="table-wrap" style={{ padding: 12, flex: 1, overflowY: 'auto', minHeight: 0 }}>
          <p className="text-center grey--text" style={{ padding: 40 }} data-testid="main-area-select-prompt">
            変換表を選択してください。
          </p>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex flex-col" style={{ flex: 1, minHeight: 0 }} data-testid="conversion-item-table">
        <div className="table-wrap" style={{ padding: 12, flex: 1, overflowY: 'auto', minHeight: 0 }}>
          <p className="text-center grey--text" style={{ padding: 40 }}>
            読み込み中…
          </p>
        </div>
      </div>
    )
  }

  const hasErrors = validationErrors && validationErrors.length > 0
  const duplicateCount = (validationErrors || []).filter((e) => e.errorType === 'duplicate').length
  const invalidCount = (validationErrors || []).filter(
    (e) => e.errorType === 'invalid' || e.errorType === 'invalid_category'
  ).length

  return (
    <div className="flex flex-col" style={{ flex: 1, minHeight: 0 }} data-testid="conversion-item-table">
      {hasErrors && (
        <div className="validation-summary p-3 my-3 mx-3 rounded-lg border border-red-200 bg-red-50 text-red-600">
          <div className="font-bold text-red-600">エラー: {validationErrors.length}件</div>
          <div className="mt-1">
            {[
              duplicateCount > 0 && `変換前の値が重複: ${duplicateCount}件`,
              invalidCount > 0 && `変換後の値が区分値に存在しません: ${invalidCount}件`
            ]
              .filter(Boolean)
              .join('、')}
            。CSVを修正して再インポートしてください。
          </div>
        </div>
      )}
      {selectedMeta && (
        <div
          className="info-bar flex flex-wrap items-center gap-3 py-2 px-3 bg-blue-50 border-b border-gray-200 text-sm"
          data-testid="conversion-info-bar"
        >
          <span className="label grey--text whitespace-nowrap">変換先区分：</span>
          <select
            className="text-input !text-left min-w-[160px] max-w-[260px] w-auto flex-none shrink-0"
            value={pendingConversionTarget}
            onChange={(e) => onConversionTargetChange(e.target.value)}
            aria-label="変換先区分"
            data-testid="main-area-conversion-target"
          >
            {classificationOptions.map((opt) => (
              <option key={opt.value || '__empty__'} value={opt.value}>
                {opt.label || 'なし'}
              </option>
            ))}
          </select>
          <span className="text-caption-2 grey--text flex-1 min-w-[200px] ml-2">
            区分を指定すると、変換後の値がその区分の値に限定されます。
          </span>
          {displayItems?.length > 0 && (
            <span className="grey--text whitespace-nowrap">（変換項目 {displayItems.length} 件）</span>
          )}
        </div>
      )}
      {/* 登録数（固定表示） */}
      <div style={{ padding: '12px 12px 8px 12px' }}>
        <p className="text-sm grey--text mb-0" data-testid="registration-count">
          登録数：{displayItems?.length ?? 0}件
        </p>
      </div>
      {/* テーブル表示エリア（仮想化スクロール：可視行のみ描画） */}
      <div className="table-wrap" style={{ padding: '0 12px 12px 12px', flex: 1, minHeight: 0 }}>
        {!displayItems || displayItems.length === 0 ? (
          <p className="text-center grey--text" style={{ padding: 40 }} data-testid="main-area-empty-message">
            データがありません。インポートしてください。
          </p>
        ) : (
          <div className="virtualized-table h-full w-full min-h-0" data-testid="conversion-items-table">
            <AutoSizer>
              {({ height, width }) => (
                <Table
                  width={width}
                  height={height}
                  headerHeight={HEADER_HEIGHT}
                  rowHeight={ROW_HEIGHT}
                  rowCount={displayItems.length}
                  rowGetter={({ index }) => displayItems[index] || {}}
                  rowClassName={rowClassName}
                  overscanRowCount={OVERSCAN_ROWS}
                  sortBy={null}
                  sortDirection={null}
                >
                  <Column
                    disableSort
                    dataKey="__no__"
                    label="No."
                    width={88}
                    className={CELL_CLASS}
                    headerClassName={CELL_CLASS}
                    headerRenderer={headerRenderer}
                    cellRenderer={noCellRenderer}
                  />
                  <Column
                    disableSort
                    dataKey="before"
                    label="変換前"
                    width={200}
                    flexGrow={1}
                    className={CELL_CLASS}
                    headerClassName={CELL_CLASS}
                    headerRenderer={headerRenderer}
                    cellRenderer={valueCellRenderer}
                  />
                  <Column
                    disableSort
                    dataKey="after"
                    label="変換後"
                    width={200}
                    flexGrow={1}
                    className={CELL_CLASS}
                    headerClassName={CELL_CLASS}
                    headerRenderer={headerRenderer}
                    cellRenderer={valueCellRenderer}
                  />
                </Table>
              )}
            </AutoSizer>
          </div>
        )}
      </div>
    </div>
  )
}

ConversionItemTable.propTypes = {
  pendingConversionTarget: PropTypes.string.isRequired,
  onConversionTargetChange: PropTypes.func.isRequired
}

export default ConversionItemTable
